//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnModel } from './../../modules/api/api.models'
import { OpActivity, OpLocationType } from '../../models/core'
import { Activity } from '../../models/common'
import { Enums } from '../../models/common/enums'
import { OpLocation } from '../../models/core/op-location'
import { OpChangeState } from '../../models/common/enums'

import { CreateEditInitParams, BulkEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { Dictionary, TypeHelper } from '../../helpers'
import { CommonHelper } from '../../helpers/common-helper'
import { LocalizationHelper } from '../../helpers/localization-helper'
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'

import { IReferenceType, IOpObject } from '../../models/util/interfaces'
import { SESSION } from '../../models/custom/session'
import { KeyType } from '../../helpers/dictionary'
import { ItemSwapperPopupOptions, IItemSwapperPopup, ItemSwapperResult } from '../../modules/common/item-swapper/'
import { ColumnInfo } from '../../models/custom';

export const GROUPING_LOCATION_SERVICE_NAME = 'groupingLocationService'
export const GROUPING_LOCATION_SERVICE_PROVIDER_NAME = 'groupingLocationServiceProvider'

export class GroupingLocationService {

    // #region Consts

    public static get FILE_NAME_PREFIX(): string { return 'grouping-locations' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdLocation }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Grouping_Location }

    // #endregion

    // #region Ctor

    constructor(private userSession: UserSessionService) { }

    // #endregion

    // #region Methods

    public deleteGroupingLocation(idLocation: number): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        const toDelete: OpLocation = new OpLocation(null)

        toDelete.idLocation = idLocation

        this.userSession
            .getDataProvider()
            .Location
            .deleteLocation(toDelete)
            .then(() => {
                CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, GroupingLocationService.SIMAX_MODULE_NAME))
                deferred.resolve()
            })
            .fail((error: any) => {
                let errorMsg: string = error

                if (SESSION.isType(error)) {
                    errorMsg = (<SESSION>error).toString()
                    CommonHelper.openInfoPopup(errorMsg, 'error', 4000)
                } else {
                    CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, GroupingLocationService.SIMAX_MODULE_NAME), 'error', 4000)
                }

                deferred.reject(errorMsg)
            })

        return deferred.promise()
    }

    public assignGroupingLocation(parentLocationId: number, swapResult: ItemSwapperResult<OpLocation>) {

        const groupingLocation: OpLocation = new OpLocation(null)
        groupingLocation.idLocation = parentLocationId

        const locations: OpLocation[] = swapResult.addedToLeft.map((added: OpLocation) => {
            return this.createHierarchyChange(parentLocationId, added.idLocation, OpChangeState.New)
        })
        .concat(swapResult.addedToRight.map((removed: OpLocation) => {
            return this.createHierarchyChange(parentLocationId, removed.idLocation, OpChangeState.Delete)
        }))

        const swapResultPaginationExists = swapResult != null && swapResult.leftPagination != null

        const columns: ColumnInfo[] = swapResultPaginationExists ? swapResult.leftPagination.columns : []
        const pageNbr: number = swapResultPaginationExists ? swapResult.leftPagination.pageNbr : 0
        const pageSize: number = swapResultPaginationExists ? swapResult.leftPagination.pageSize : 0
        const customWhereClause: string = swapResultPaginationExists ? swapResult.leftPagination.customWhereClause : null

        return this.userSession
            .getDataProvider()
            .GroupingLocation
            .assignGroupingLocation(groupingLocation, locations, columns, pageNbr, pageSize, customWhereClause)
    }

    public saveAssignedLocations(parentLocationId: number, swapResult: ItemSwapperResult<OpLocation>): JQueryPromise<OpLocation> {

        let promise: JQueryPromise<any> = $.Deferred().resolve().promise()

        const addLocationAction: (added: OpLocation) => JQueryPromise<OpLocation> = (added: OpLocation) => {
            const childLocation: OpLocation = this.createHierarchyChange(parentLocationId, added.idLocation, OpChangeState.New)

            return this.userSession
                .getDataProvider()
                .Location
                .saveLocation(childLocation)
        }

        const removeLocationAction: (removed: OpLocation) => JQueryPromise<OpLocation> = (removed: OpLocation) => {
            const childLocation: OpLocation = this.createHierarchyChange(parentLocationId, removed.idLocation, OpChangeState.Delete)

            return this.userSession
                .getDataProvider()
                .Location
                .saveLocation(childLocation)
        }

        swapResult.addedToLeft.forEach((added: OpLocation) => {
            promise = promise.then(() => addLocationAction(added))
        })

        swapResult.addedToRight.forEach((removed: OpLocation) => {
            promise = promise.then(() => removeLocationAction(removed))
        })

        return promise.then((location: OpLocation) => {
            CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, GroupingLocationService.SIMAX_MODULE_NAME))
            return null
        })
            .fail((error: any) => {
                let errorMsg: string = error

                if (SESSION.isType(error)) {
                    errorMsg = (<SESSION>error).toString()
                    CommonHelper.openInfoPopup(errorMsg, 'error', 4000)
                } else {
                    CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, GroupingLocationService.SIMAX_MODULE_NAME), 'error', 4000)
                }

                return errorMsg
            })
    }

    public saveGroupingLocation(location: OpLocation): JQueryPromise<OpLocation> {
        const deferred: JQueryDeferred<OpLocation> = $.Deferred<OpLocation>()

        this.userSession
            .getDataProvider()
            .GroupingLocation
            .saveGroupingLocation(location)
            .then((savedObject: OpLocation) => {
                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                let errorMsg: string = error

                if (SESSION.isType(error)) {
                    errorMsg = (<SESSION>error).toString()
                }

                deferred.reject(errorMsg)
            })

        return deferred.promise()
    }

    public getDefaultAssignedLocationsItemSwapperOptions(
        moduleName: Enums.SimaxModuleName,
        referenceType: Enums.ReferenceType
    ): JQueryPromise<ItemSwapperPopupOptions<OpLocation>> {
        return this.userSession
            .getDataProvider()
            .System
            .getRequestColumnInfo(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__ASSIGNED_LOCATION_GRID_PARAMS,
                moduleName
            )
            .then((columns: ColumnModel[]): ItemSwapperPopupOptions<OpLocation> => {
                const itemSwapperPopupOptions: ItemSwapperPopupOptions<OpLocation> = {
                    referenceType,

                    itemSwapperOptions: {
                        rightGridOptions: {
                            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AvailableLocations),

                            dataBag: true,

                            columnsDictionary: columns,
                        },

                        leftGridOptions: {
                            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedLocations),

                            columnsDictionary: columns,
                        }
                    },

                    onInitialized: (itemSwapper: IItemSwapperPopup<OpLocation>) => {
                        return itemSwapper
                    },

                    itemSwapperPopupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedLocations)
                }

                return itemSwapperPopupOptions
            })
    }

    // #endregion

    // #region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): JQueryPromise<CreateEditInitParams> {
        return this.getAvailableGroupingLocationTypes(Enums.ReferenceType.IdLocationType)
            .then((locationTypes: Dictionary<OpLocationType[]>) => {
                return <CreateEditInitParams>{
                    createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor,
                    idReferenceType: GroupingLocationService.REFERENCE_TYPE,
                    moduleName: GroupingLocationService.SIMAX_MODULE_NAME,
                    sections: null,
                    useBusinessObjects: true,

                    referenceObject,
                    referenceObjectId,

                    customDataSources: locationTypes
                }
            })
    }

    public getBulkEditInitOptions(columns: any[], selectedGridItems: any[]): JQueryPromise<BulkEditInitParams> {
        return this.getAvailableGroupingLocationTypes(Enums.ReferenceType.IdLocationType)
            .then((locationTypes: Dictionary<OpLocationType[]>) => {
                return {
                    gridColumns: columns,
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor,
                    idReferenceType: GroupingLocationService.REFERENCE_TYPE,
                    moduleName: GroupingLocationService.SIMAX_MODULE_NAME,
                    section: null,
                    selectedGridRows: TypeHelper.clone(selectedGridItems),
                    useBusinessObjects: true,

                    customDataSources: locationTypes
                }
            })
    }

    // #endregion

    // #region Private

    private createHierarchyChange(parentLocationId: number, editedLocationId: number, changeState: OpChangeState): OpLocation {
        const childLocation: OpLocation = new OpLocation(null)
        const parentLocation: OpLocation = new OpLocation(null)

        parentLocation.idLocation = parentLocationId
        parentLocation.opState = changeState

        childLocation.idLocation = editedLocationId
        childLocation.opState = OpChangeState.Modified

        childLocation.parentGroups.push(parentLocation)

        return childLocation
    }

    public getAvailableGroupingLocationTypes(referenceId: number): JQueryPromise<Dictionary<OpLocationType[]>> {
        return this.userSession
            .getDataProvider()
            .System
            .getDictionaries([referenceId])
            .then((referenceDictionary: Dictionary<Dictionary<OpLocationType>>) => {
                const referenceLocationTypes: Dictionary<OpLocationType> = referenceDictionary.tryGetValue(Enums.ReferenceType.IdLocationType)


                let hasAllPermission: boolean = false
                const referenceValues: number[] = _.filter(this.userSession
                    .getLoggedOperator()
                    .activities, (activity: OpActivity) => { return activity.idActivity === Activity.ALLOWED_GROUPING_LOCATION_TYPE && activity.deny === false })
                    .map((activity: OpActivity) => {
                        if (activity.referenceValue == null) {
                            hasAllPermission = true
                        }
                        return activity.referenceValue
                    })

                if (hasAllPermission) {
                    return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: referenceLocationTypes.values() }], KeyType.Number)
                } else {
                    if (_.size(referenceValues) === 0) {
                        const defaultLocationType: OpLocationType = referenceLocationTypes.tryGetValue(Enums.LocationType.GroupingLocation)
                        return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: [defaultLocationType] }], KeyType.Number)
                    } else {
                        const filteredTypes: OpLocationType[] = []
                        const locationTypes: OpLocationType[] = referenceLocationTypes.values()

                        referenceValues.forEach((value: number) => {
                            locationTypes.forEach((item: OpLocationType) => {
                                if (value === item.idLocationType) {
                                    filteredTypes.push(item)
                                }
                            })
                        })
                        return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: filteredTypes }], KeyType.Number)
                    }
                }
            })
    }

    // #endregion
}

export class GroupingLocationServiceProvider implements ng.IServiceProvider {

    // #region IServiceProvider Implementation 

    public $get = [USER_SESSION_SERVICE_NAME, function (userSession: UserSessionService) {
        return new GroupingLocationService(userSession)
    }]

    // #endregion
} 